import { isEqual } from 'es-toolkit';

import { AssistantCaller, ComponentDesign } from '@paragon/contract-library';

import type { RootState } from '../store';
import { getActiveComponentDesign } from '../store/selectors/getActiveComponentDesignSelector';
import {
  describeBearingGeometry,
  describeLumber,
  describeMemberGeometry,
  getTrussMembers,
  roundPoint,
} from './tools/helpers';

/**
 * What a caller last saw of the project: the trusses and layout, with their undo histories, when its last tool call
 * ended.
 */
interface WhatWasSeen {
  readonly openContext: RootState['application']['openContext'];
  readonly openTrussGuid: string | null;
  readonly componentDesigns: RootState['componentDesigns']['data'];
  readonly undoRedoChains: RootState['editor']['undoRedoChains'];
  readonly layout: RootState['layout'];
}

const seenBy = new Map<AssistantCaller, WhatWasSeen>();

const MAX_CHANGES_PER_TRUSS = 20;

/** The layout's kinds of items the designer can undo changes to, as a designer would name them. */
const LAYOUT_ITEM_KINDS = {
  bearingEnvelopes: 'wall or beam',
  ceilingPlanes: 'ceiling plane',
  ceilingTrays: 'ceiling tray',
  featurePatternContainers: 'floor area',
  layoutLoads: 'layout load',
  roofContainers: 'roof',
  roofPlanes: 'roof plane',
  trussEnvelopes: 'truss envelope',
  workLines: 'work line',
} as const satisfies Partial<Record<keyof RootState['layout'], string>>;

/**
 * Remembers the project as `caller` sees it now, so its next call is told only what changed after this.
 */
export function rememberWhatWasSeen(caller: AssistantCaller, state: RootState) {
  seenBy.set(caller, {
    openContext: state.application.openContext,
    openTrussGuid: getActiveComponentDesign(state)?.guid ?? null,
    componentDesigns: state.componentDesigns.data,
    undoRedoChains: state.editor.undoRedoChains,
    layout: state.layout,
  });
}

/**
 * Notes that `caller` itself opened the truss that's open now, such as to show a Plan's preview, so it isn't reported
 * as the designer's doing.
 */
export function rememberOpenTruss(caller: AssistantCaller, state: RootState) {
  const seen = seenBy.get(caller);
  if (seen != null) seenBy.set(caller, { ...seen, openTrussGuid: getActiveComponentDesign(state)?.guid ?? null });
}

export function forgetWhatWasSeen(caller: AssistantCaller) {
  seenBy.delete(caller);
}

/**
 * What changed in the app since `caller`'s last tool call ended, in a designer's words: the designer's own edits, and
 * any another Assistant or Agent made. Only changes that can be undone count, so the server's replies to saves and
 * analyses don't. Returns `null` when nothing changed, or when `caller` hasn't seen the project yet.
 */
export function describeChangesSinceLastSeen(caller: AssistantCaller, state: RootState) {
  const seen = seenBy.get(caller);
  if (seen == null) return null;

  if (!isSameContext(state.application.openContext, seen.openContext))
    return { opened: 'A different project or context was opened. Read it again before changing anything.' };

  const trusses: { truss: string; changes: string[] }[] = [];
  for (const [guid, now] of Object.entries(state.componentDesigns.data)) {
    const before = seen.componentDesigns[guid];
    if (before == null || before === now || state.editor.undoRedoChains[guid] === seen.undoRedoChains[guid]) continue;
    const changes = describeTrussChanges(before, now);
    if (changes.length > 0) trusses.push({ truss: now.component.name, changes });
  }

  const layout = state.layout.undoRedoChain === seen.layout.undoRedoChain ? [] : describeLayoutChanges(seen, state);

  const openTruss = getActiveComponentDesign(state);
  const switched = (openTruss?.guid ?? null) !== seen.openTrussGuid;

  if (trusses.length === 0 && layout.length === 0 && !switched) return null;
  return {
    ...(switched ? { nowOpen: openTruss == null ? 'the layout' : `the truss ${openTruss.component.name}` } : {}),
    ...(trusses.length > 0 ? { trusses } : {}),
    ...(layout.length > 0 ? { layout } : {}),
  };
}

function isSameContext(a: RootState['application']['openContext'], b: RootState['application']['openContext']) {
  return a === b || JSON.stringify(a) === JSON.stringify(b);
}

const formatPoint = (point: { x: number; y: number }) => {
  const { x, y } = roundPoint(point);
  return `(${x}, ${y})`;
};

/**
 * A member's centerline from end to end, or `null` if the app can't compute it.
 */
function describeCenterline(componentDesign: ComponentDesign, memberGuid: string) {
  const member = getTrussMembers(componentDesign).find((candidate) => candidate.guid === memberGuid);
  const centerline = member == null ? null : describeMemberGeometry(member, componentDesign.component).centerline;
  return centerline == null ? null : `${formatPoint(centerline.from)} to ${formatPoint(centerline.to)}`;
}

/**
 * What changed on one truss, member by member, then its bearings, splices, plies, loads, and locks.
 */
export function describeTrussChanges(before: ComponentDesign, now: ComponentDesign): string[] {
  const changes: string[] = [];
  if (before.component.name !== now.component.name)
    changes.push(`Renamed from ${before.component.name} to ${now.component.name}.`);

  const membersBefore = new Map(getTrussMembers(before).map((member) => [member.guid, member]));
  const membersNow = new Map(getTrussMembers(now).map((member) => [member.guid, member]));

  // The app renumbers members whenever one is added or deleted, so renames come as one line, after what caused them.
  const renames: string[] = [];
  for (const [guid, member] of membersNow) {
    const old = membersBefore.get(guid);
    if (old == null) {
      const at = describeCenterline(now, guid);
      changes.push(`Added ${member.name}, ${describeLumber(member)}${at == null ? '' : `, from ${at}`}.`);
      continue;
    }
    if (old.name !== member.name) renames.push(`${old.name} is now ${member.name}`);
    if (describeLumber(old) !== describeLumber(member))
      changes.push(`Changed ${member.name} from ${describeLumber(old)} to ${describeLumber(member)}.`);
    const wasAt = describeCenterline(before, guid);
    const isAt = describeCenterline(now, guid);
    if (wasAt !== isAt && isAt != null) changes.push(`Moved ${member.name}: it was ${wasAt}, now ${isAt}.`);
  }
  for (const [guid, member] of membersBefore)
    if (!membersNow.has(guid)) changes.push(`Deleted ${member.name} (its name before any renumbering).`);
  if (renames.length > 0) changes.push(`Renumbered members: ${renames.join(', ')}.`);

  const bearingsBefore = new Map(before.component.bearings.map((bearing) => [bearing.guid, bearing]));
  for (const bearing of now.component.bearings) {
    const old = bearingsBefore.get(bearing.guid);
    const isAt = describeBearingGeometry(bearing, now.component);
    if (old == null) {
      changes.push(`Added bearing ${bearing.name}${isAt.center == null ? '' : ` at ${formatPoint(isAt.center)}`}.`);
      continue;
    }
    const wasAt = describeBearingGeometry(old, before.component);
    if (JSON.stringify(wasAt) !== JSON.stringify(isAt))
      changes.push(
        `Moved or resized bearing ${bearing.name}` +
          (isAt.center == null ? '.' : `: now centered at ${formatPoint(isAt.center)}, ${isAt.width} in. wide.`),
      );
  }
  for (const bearing of before.component.bearings)
    if (!now.component.bearings.some((candidate) => candidate.guid === bearing.guid))
      changes.push(`Deleted bearing ${bearing.name}.`);

  const splicesBefore = new Map(before.component.chordSplices.map((splice) => [splice.guid, splice]));
  for (const splice of now.component.chordSplices) {
    const old = splicesBefore.get(splice.guid);
    const at = formatPoint({ x: splice.centerPointX, y: splice.centerPointY });
    if (old == null) changes.push(`Added splice ${splice.name} at ${at}.`);
    else if (old.centerPointX !== splice.centerPointX || old.centerPointY !== splice.centerPointY)
      changes.push(`Moved splice ${splice.name} to ${at}.`);
  }
  for (const splice of before.component.chordSplices)
    if (!now.component.chordSplices.some((candidate) => candidate.guid === splice.guid))
      changes.push(`Deleted splice ${splice.name}.`);

  const pliesBefore = 'numberOfPlies' in before.component ? before.component.numberOfPlies : null;
  const pliesNow = 'numberOfPlies' in now.component ? now.component.numberOfPlies : null;
  if (pliesBefore !== pliesNow) changes.push(`Changed the plies from ${pliesBefore} to ${pliesNow}.`);
  const spacingBefore = 'spacing' in before.component ? before.component.spacing : null;
  const spacingNow = 'spacing' in now.component ? now.component.spacing : null;
  if (spacingBefore !== spacingNow) changes.push(`Changed the spacing from ${spacingBefore} to ${spacingNow} in.`);

  // A save's reply brings back equal copies of these, so they're compared by value.
  if (!isEqual(before.additionalLoads, now.additionalLoads) || before.loadSetGuid !== now.loadSetGuid)
    changes.push('Changed its loads or loading settings.');
  if (!isEqual(before.materialSelection, now.materialSelection)) changes.push('Changed its material selection.');

  const locks = (design: ComponentDesign) =>
    JSON.stringify([
      design.membersLocked,
      design.lockedMembers,
      design.platingLocked,
      design.lockedPlatePairs,
      design.loadingLocked,
      design.analogLocked,
      design.hangersLocked,
      design.lockedHangers,
    ]);
  if (locks(before) !== locks(now)) changes.push('Changed what is locked.');

  if (changes.length === 0 && !isEqual(before.component.platePairs, now.component.platePairs))
    changes.push('Changed its plates.');

  if (changes.length > MAX_CHANGES_PER_TRUSS)
    return [
      ...changes.slice(0, MAX_CHANGES_PER_TRUSS),
      `And ${changes.length - MAX_CHANGES_PER_TRUSS} more changes; read the truss again with get_truss.`,
    ];
  return changes;
}

/**
 * How many of each kind of layout item were added, deleted, or changed.
 */
function describeLayoutChanges(seen: WhatWasSeen, state: RootState) {
  const changes: string[] = [];
  for (const [key, kind] of Object.entries(LAYOUT_ITEM_KINDS) as [keyof typeof LAYOUT_ITEM_KINDS, string][]) {
    const before: Record<string, unknown> = seen.layout[key];
    const now: Record<string, unknown> = state.layout[key];
    if (before === now) continue;

    const added = Object.keys(now).filter((guid) => !(guid in before)).length;
    const deleted = Object.keys(before).filter((guid) => !(guid in now)).length;
    const changed = Object.keys(now).filter((guid) => guid in before && before[guid] !== now[guid]).length;
    const count = (number: number, verb: string) =>
      number === 0 ? [] : [`${verb} ${number} ${kind}${number === 1 ? '' : 's'}`];
    changes.push(...count(added, 'Added'), ...count(deleted, 'Deleted'), ...count(changed, 'Changed'));
  }
  return changes.map((change) => `${change}.`);
}
